import { first, isEmpty, map, result } from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import {
  Radio,
  RadioGroup,
  Label,
  Dropdown,
  Option,
  Checkbox,
} from '@fluentui/react-components';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-message-bar';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { campaignLanguageStrings } from '@bingads-webui-campaign-react/language-target';
import { withControlErrorHandling, withUnvalidatedControlErrors, withContainerErrors, FormErrorBeaconAdapter } from '@bingads-webui-campaign-react/error-handling';
import { getDSAWebsiteLanguages, targetingSourceOptions, targetingSourceOptionStrings, ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import { PageFeedSelectorSection } from '@bingads-webui-campaign-react/page-feed-selector';
import {
  andComp,
  entityIs,
  propertyIs,
  StandardControlAdapter,
} from '@bingads-webui-campaign-react/form-error';
import { FocusableActuator } from '@bingads-webui-campaign-react/actuator';

import { ValidatedNoProtocolWebsite as WebsiteControl } from './validated-no-protocol-website';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const feedAssociationsProp = andComp(entityIsCampaign, propertyIs('FeedAssociations'));
const ServerValidatedPageFeedSelectorSection = withControlErrorHandling(withContainerErrors(PageFeedSelectorSection, { filter: feedAssociationsProp, Adapter: StandardControlAdapter() }), [FocusableActuator], [FormErrorBeaconAdapter]);

const domainProp = andComp(entityIsCampaign, propertyIs('DomainName'));
const ValidatedWebsite = withControlErrorHandling(withUnvalidatedControlErrors(WebsiteControl, { filter: domainProp }));

const getStyles = () =>
  ({
    dynamicSetting: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      '& .ad-group-website-label': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .ad-group-website-language-label': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .website-language-dropdown': {
        width: '320px',
      },
      '& .ad-group-targeting-source-label': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .page-feed-selector': {
        display: 'flex',
        marginLeft: '36px',
      },
      '& .message-bar': {
        marginTop: '20px',
      },
    },
  });

const ValidatedDynamicWebsite = ({
  websites,
  disabled,
}) => {
  const i18n = useContext(I18nContext);

  return (
    <>
      <div className="ad-group-website-label">
        <Label weight="semibold">
          {i18n.getString(_TL_('Website'))}
        </Label>
        <HelpPopup
          topic="pop_BA_Search_Website"
          aria-label={i18n.getString(_TL_('Click to learn more about website'))}
        />
      </div>
      <ValidatedWebsite
        validationProps={{
          data: websites || [],
          debounce: 700,
        }}
        validateOnRender
        disabled={disabled}
        useFluentV2
        isEmptyValid={false}
      />
      <p>{i18n.getString(_TL_("Your website and website language cannot be changed once they're saved."))}</p>
    </>
  );
};

ValidatedDynamicWebsite.propTypes = {
  websites: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
};

ValidatedDynamicWebsite.defaultProps = {
  disabled: false,
};

const DynamicWebsiteLanguage = ({
  validationProps,
  updateValidation,
  permissions,
  disabled,
}) => {
  const { data: selectedWebsiteLanguage } = validationProps;
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const websiteLanguages = getDSAWebsiteLanguages(permissions);

  const onWebsiteLanguageOptionSelect = useCallback((_evt, option) => {
    const newSelectedWebsiteLanguage = first(option.selectedOptions);
    if (newSelectedWebsiteLanguage !== selectedWebsiteLanguage) {
      updateValidation([], { selectedWebsiteLanguage: newSelectedWebsiteLanguage });
      activity.signal(`onDSAWebsiteLanguageChange - ${newSelectedWebsiteLanguage}`);
    }
  }, [activity, selectedWebsiteLanguage, updateValidation]);

  return (
    <>
      {
        selectedWebsiteLanguage && (
          <>
            <div className="ad-group-website-language-label">
              <Label weight="semibold">
                {i18n.getString(_TL_('Website language'))}
              </Label>
              <HelpPopup
                topic="pop_BA_Search_WebsiteLanguage"
                aria-label={i18n.getString(_TL_('Click to learn more about Website language'))}
              />
            </div>
            {
              websiteLanguages.length > 1 ? (
                <Dropdown
                  className="website-language-dropdown"
                  aria-label={i18n.getString(_TL_('Website language'))}
                  value={i18n.getString(campaignLanguageStrings[selectedWebsiteLanguage] || selectedWebsiteLanguage)}
                  selectedOptions={[selectedWebsiteLanguage]}
                  onOptionSelect={onWebsiteLanguageOptionSelect}
                  disabled={disabled}
                >
                  {map(websiteLanguages, websiteLanguage => (
                    <Option
                      key={websiteLanguage}
                      value={websiteLanguage}
                    >
                      {i18n.getString(campaignLanguageStrings[websiteLanguage] || websiteLanguage)}
                    </Option>
                ))}
                </Dropdown>
              ) : <div>{websiteLanguages[0]}</div>
            }
          </>
        )}
    </>
  );
};

DynamicWebsiteLanguage.propTypes = {
  validationProps: {
    data: PropTypes.string,
  }.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      website: PropTypes.shape({
        enabled: PropTypes.bool,
      }),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

DynamicWebsiteLanguage.defaultProps = {
  disabled: false,
};

const ValidatedDynamicWebsiteLanguage = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(DynamicWebsiteLanguage))));

const DynamicTargetingSource = ({
  validationProps,
  updateValidation,
}) => {
  const { selectedTargetingSource, pageFeeds = [] } = validationProps;

  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const { CurrentAccount } = serverContext || {};
  const { AdvertiserCustomerID: advertiserCustomerId } = CurrentAccount || {};

  const verifyTargetingSource = useCallback(({
    newSelectedTargetingSource = selectedTargetingSource,
    newPageFeeds = pageFeeds,
  }) => (
    newSelectedTargetingSource !== targetingSourceOptions.SystemIndex && isEmpty(newPageFeeds) ?
      [i18n.getString(_TL_('Please select at least one page feed'))] : []
  ), [i18n, pageFeeds, selectedTargetingSource]);

  const onTargetingSourceOptionSelect = useCallback((_evt, option) => {
    if (option.value !== selectedTargetingSource) {
      const newSelectedTargetingSource = option.value;
      const errors = verifyTargetingSource({ newSelectedTargetingSource });
      updateValidation(errors, { selectedTargetingSource: newSelectedTargetingSource });
      activity.signal(`onDSATargetingSourceChange - ${newSelectedTargetingSource}`);
    }
  }, [activity, selectedTargetingSource, updateValidation, verifyTargetingSource]);

  const onSelectedPageFeedsChange = useCallback((selectedPageFeeds) => {
    const newPageFeeds = selectedPageFeeds || [];
    const errors = verifyTargetingSource({ newPageFeeds });
    updateValidation(errors, { pageFeeds: newPageFeeds });
    activity.signal(`onSelectedPageFeedsChange - ${newPageFeeds}`);
  }, [activity, updateValidation, verifyTargetingSource]);

  return (
    <>
      <div className="ad-group-targeting-source-label">
        <Label weight="semibold">
          {i18n.getString(_TL_('Targeting source'))}
        </Label>
        <HelpPopup
          topic="pop_BA_DSA_TargetingSource"
          aria-label={i18n.getString(_TL_('Click to learn more about Targeting source'))}
        />
      </div>
      <RadioGroup className="targeting-source-radiogroup" value={selectedTargetingSource} onChange={onTargetingSourceOptionSelect}>
        {map(targetingSourceOptions, (targetingSourceOption, index) => (
          <Radio
            key={targetingSourceOption}
            value={targetingSourceOption}
            label={i18n.getString(targetingSourceOptionStrings[index])}
          />
        ))}
      </RadioGroup>
      {
        selectedTargetingSource !== targetingSourceOptions.SystemIndex &&
        <ServerValidatedPageFeedSelectorSection
          cid={advertiserCustomerId}
          aid={scope.values.aid}
          campaignId={scope.values.campaignId}
          customizedDescription={i18n.getString(_TL_('Page feed'))}
          customizedSearchPlaceholder={i18n.getString(_TL_('Search for page feeds'))}
          loadAssociatedPageFeedsFactory={() => () => Promise.resolve(pageFeeds)}
          onChange={onSelectedPageFeedsChange}
        />
      }
    </>
  );
};

DynamicTargetingSource.propTypes = {
  validationProps: {
    selectedTargetingSource: PropTypes.string,
    pageFeeds: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string })),
  }.isRequired,
  updateValidation: PropTypes.func.isRequired,
};

const ValidatedDynamicTargetingSource = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(DynamicTargetingSource)), { filter: feedAssociationsProp }));

const DynamicSearchText = ({
  validationProps,
  updateValidation,
  permissions,
}) => {
  const { dynamicTextEnabled } = validationProps;
  const isDynamicDescriptionPhase1Enabled = result(permissions.dynamic, 'DSADynamicDescriptionPhase1');

  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const onDynamicTextChange = useCallback((_evt, option) => {
    if (option.checked !== dynamicTextEnabled) {
      updateValidation([], { dynamicTextEnabled: option.checked });
      activity.signal(`onDynamicTextEnableChange - ${option.checked}`);
    }
  }, [activity, dynamicTextEnabled, updateValidation]);

  return (
    <>
      {
        isDynamicDescriptionPhase1Enabled && (
          <Checkbox
            className="dynamic-description-switch"
            checked={dynamicTextEnabled}
            onChange={onDynamicTextChange}
            label={i18n.getString(_TL_('Enable Dynamic Search ad text'))}
          />
        )
      }
    </>
  );
};

DynamicSearchText.propTypes = {
  validationProps: {
    dynamicTextEnabled: PropTypes.bool,
  }.isRequired,
  updateValidation: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      DSADynamicDescriptionPhase1: PropTypes.bool,
    }),
  }).isRequired,
};

const ValidatedDynamicSearchText = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(DynamicSearchText))));

export const ValidatedDynamicSearchAdSetting = ({
  data, disabled, isMessageBarVisible,
}) => {
  const {
    websites,
    selectedWebsiteLanguage,
    selectedTargetingSource,
    dynamicTextEnabled,
    pageFeeds,
  } = data;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const serverContext = useContext(ServerContext);

  const {
    DynamicPermissions,
    Permissions,
  } = serverContext;
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  return (
    <div className={classnames('dynamic-setting-section', classes.dynamicSetting)}>
      <ValidatedDynamicWebsite
        websites={websites}
        disabled={disabled}
      />
      <ValidatedDynamicWebsiteLanguage
        validationProps={{
          data: selectedWebsiteLanguage,
        }}
        permissions={permissions}
        disabled={disabled}
      />
      <ValidatedDynamicTargetingSource
        validationProps={{
          selectedTargetingSource,
          pageFeeds,
        }}
      />
      <ValidatedDynamicSearchText
        validationProps={{
          dynamicTextEnabled,
        }}
        permissions={permissions}
      />
      {isMessageBarVisible && (
        <MessageBar className="message-bar">
          <MessageBarBody>
            <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
            {i18n.getString(_TL_('Dynamic ad groups contain Dynamic Search ads and dynamic ad targets.'))}
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

ValidatedDynamicSearchAdSetting.propTypes = {
  data: PropTypes.shape({
    websites: PropTypes.arrayOf(PropTypes.string),
    selectedWebsiteLanguage: PropTypes.string,
    selectedTargetingSource: PropTypes.string,
    dynamicTextEnabled: PropTypes.bool,
    pageFeeds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  disabled: PropTypes.bool,
  isMessageBarVisible: PropTypes.bool,
};

ValidatedDynamicSearchAdSetting.defaultProps = {
  disabled: false,
  isMessageBarVisible: true,
};
